import R from "ramda";
import React, {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { isApolloError, useLazyQuery } from "@apollo/client";
import TestCardService from "@app/service/testCard";
import { NotificationContext, TestCardUtilsContext } from "@app/provider";
import {
  EmpowerRiskAlertInfo,
  ParsedTestCard,
  SecondaryCard,
  TestCard,
} from "@app/provider/testData/types";
import { useIntl } from "react-intl";
import { AddNotificationParameters } from "@app/provider/notification";
import { getExtendedServerErrorNotificationParams } from "@app/utils/getExtendedServerErrorNotificationParams";
import { requireGraphQLErrorBody } from "@app/provider/error/graphQLErrorCode";

export interface TestCardController {
  isLoading: boolean;
  testCards: ParsedTestCard[] | undefined;
  secondaryActionCards: SecondaryCard[];
  getTestsDataForHomePage: () => Promise<ParsedTestCard[]>;
  getPrimaryTestCards: (options?: {
    includeCompleted?: boolean;
  }) => Promise<ParsedTestCard[]>;
  primaryTestCardsLoading: boolean;
  isTestsHistoryExists: boolean | undefined;
  empowerRiskAlertInfo: EmpowerRiskAlertInfo | undefined;
  empowerRiskAlertInfoLoading: boolean;
  getEmpowerRiskAlertInfo: () => Promise<void>;
}

export const Context = createContext<TestCardController>({
  isLoading: false,
  testCards: undefined,
  secondaryActionCards: [],
  getTestsDataForHomePage: async () => [],
  getPrimaryTestCards: async () => [],
  primaryTestCardsLoading: false,
  isTestsHistoryExists: undefined,
  empowerRiskAlertInfo: undefined,
  empowerRiskAlertInfoLoading: false,
  getEmpowerRiskAlertInfo: async () => undefined,
});

Context.displayName = "TestCardContext";

const TestCardProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const intl = useIntl();

  const rerenderCardData = () => {
    const result = R.map(
      (testCard) => getTestCardData(testCard),
      testCardsData
    );
    setParsedTestCards(result);

    setSecondaryActionCards(
      R.map(
        (secondaryCard) => getSecondaryActionCardData(secondaryCard),
        secondaryActionCards
      )
    );
  };

  const { getTestCardData, getSecondaryActionCardData } =
    useContext(TestCardUtilsContext);

  const [testCardsData, setTestCardsData] = useState<TestCard[]>([]);
  const [parsedTestCards, setParsedTestCards] = useState<
    ParsedTestCard[] | undefined
  >(undefined);
  const [secondaryActionCards, setSecondaryActionCards] = useState<
    SecondaryCard[]
  >([]);
  const [isTestsHistoryExists, setIsTestsHistoryExists] = useState<
    boolean | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [empowerRiskAlertInfo, setEmpowerRiskAlertInfo] = useState<
    EmpowerRiskAlertInfo | undefined
  >(undefined);

  const { addNotification } = useContext(NotificationContext);

  const [getTestsDataForHomePageQuery] = useLazyQuery(
    TestCardService.getTestsDataForHomePage(),
    {
      fetchPolicy: "no-cache",
    }
  );

  const [getPrimaryTestCardsQuery, { loading: primaryTestCardsLoading }] =
    useLazyQuery(TestCardService.getTestCards(), {
      fetchPolicy: "no-cache",
    });

  const [
    getEmpowerRiskAlertInfoQuery,
    { loading: empowerRiskAlertInfoLoading },
  ] = useLazyQuery(TestCardService.getEmpowerRiskAlertInfo(), {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    rerenderCardData();
  }, [intl.locale, testCardsData.length, secondaryActionCards.length]);

  const getPrimaryTestCards = async (options?: {
    includeCompleted?: boolean;
  }) => {
    const { data, error } = await getPrimaryTestCardsQuery({
      variables: { includeCompleted: options?.includeCompleted },
    });

    if (error) {
      const notificationParams: AddNotificationParameters =
        getExtendedServerErrorNotificationParams(error);
      addNotification(notificationParams);
      return [];
    }

    const testCards: TestCard[] = data?.getTestCards;

    return R.map((testCard) => getTestCardData(testCard), testCards);
  };

  const getTestsDataForHomePage = async () => {
    let result: ParsedTestCard[] = [];

    setIsLoading(true);
    const { data, error } = await getTestsDataForHomePageQuery();

    setIsLoading(false);

    if (error) {
      const notificationParams: AddNotificationParameters =
        getExtendedServerErrorNotificationParams(error);
      addNotification(notificationParams);
      return result;
    }

    const { testCards, secondaryCards, isUserHasArchivedTests } =
      data?.getTestsDataForHomePage || {};

    if (testCards) {
      result = R.map((testCard) => getTestCardData(testCard), testCards);
      setTestCardsData(testCards);
      setParsedTestCards(result);
    }

    if (secondaryCards) {
      setSecondaryActionCards(
        R.map(
          (secondaryCard) => getSecondaryActionCardData(secondaryCard),
          secondaryCards
        )
      );
    }

    if (isUserHasArchivedTests) {
      setIsTestsHistoryExists(isUserHasArchivedTests);
    }

    return result;
  };

  const handleEmpowerRiskAlertInfo = async () => {
    try {
      const { data, error } = await getEmpowerRiskAlertInfoQuery();

      if (error) {
        throw error;
      }

      if (data?.getEmpowerRiskAlertInfo) {
        setEmpowerRiskAlertInfo(data.getEmpowerRiskAlertInfo);
      }
    } catch (error) {
      if (isApolloError(error) && error.graphQLErrors?.length > 0) {
        throw requireGraphQLErrorBody(error);
      } else {
        addNotification({
          type: "error",
        });
      }
    }
  };

  const testCardController: TestCardController = useMemo(
    () => ({
      isLoading,
      testCards: parsedTestCards,
      getTestsDataForHomePage,
      getPrimaryTestCards,
      primaryTestCardsLoading,
      secondaryActionCards,
      isTestsHistoryExists,
      empowerRiskAlertInfo,
      empowerRiskAlertInfoLoading,
      getEmpowerRiskAlertInfo: handleEmpowerRiskAlertInfo,
    }),
    [
      isLoading,
      parsedTestCards,
      getTestsDataForHomePage,
      getPrimaryTestCards,
      primaryTestCardsLoading,
      secondaryActionCards,
      isTestsHistoryExists,
      empowerRiskAlertInfo,
      empowerRiskAlertInfoLoading,
      handleEmpowerRiskAlertInfo,
    ]
  );

  return (
    <Context.Provider value={testCardController}>{children}</Context.Provider>
  );
};

export default TestCardProvider;
